import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Form, Button, Row, Col } from 'react-bootstrap';
import AdminDataTable from './AdminDataTable';
import Swal from 'sweetalert2';
import { FaEdit, FaTrash, FaPlus, FaUserFriends } from 'react-icons/fa';
import Navigation from './Navigation';
import AdminPageHeader from './AdminPageHeader';

function MemberModules() {
    const [modules, setModules] = useState([]);
    const [filteredModules, setFilteredModules] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalRows, setTotalRows] = useState(0);

    const [outletMap, setOutletMap] = useState({});
    const [memberMap, setMemberMap] = useState({});
    const [moduleMap, setModuleMap] = useState({});
    const [selectedOutlet, setSelectedOutlet] = useState('');

    const navigate = useNavigate();

    useEffect(() => {
        const token = localStorage.getItem('sessionToken');
        if (!token) {
            navigate('/loginadmin');
            return;
        }

        const fetchData = async () => {
            await fetchOutlets(token);
            await fetchMembers(token);
            await fetchModules(token);
        };

        fetchData();
    }, [navigate]);

    useEffect(() => {
        const token = localStorage.getItem('sessionToken');
        if (!token) return;

        if (Object.keys(memberMap).length > 0) {
            fetchMemberModules(token, page, selectedOutlet);
        }
    }, [memberMap, page, selectedOutlet]);

    /** ✅ Fetch Outlets */
    const fetchOutlets = async (token) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}outletlist`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ limit: 100, start: 0 })
            });
            const data = await response.json();
            
            if (data.error === "0" && Array.isArray(data.results)) {
                const outletMapping = { "": "All Outlets" };
                data.results.forEach(outlet => {
                    outletMapping[outlet.id] = outlet.outlet_name; // ✅ Fix: Use `id` and `outlet_name` from API
                });
                setOutletMap(outletMapping);
            }
        } catch (err) {
            console.error('Failed to fetch outlets:', err);
        }
    };
    

    /** ✅ Fetch Members */
    const fetchMembers = async (token) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}member/list`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ limit: 100, start: 0 })
            });
            const data = await response.json();
            if (data.error === 0 && Array.isArray(data.results)) {
                const memberMapping = {};
                data.results.forEach(member => memberMapping[member.id] = `${member.first_name} ${member.last_name}`);
                setMemberMap(memberMapping);
            }
        } catch (err) {
            console.error('Failed to fetch members:', err);
        }
    };

    /** ✅ Fetch Modules */
    const fetchModules = async (token) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}modulelist`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ limit: 100, start: 0 })
            });
            const data = await response.json();
            if (data.error === "0" && Array.isArray(data.results)) {
                const moduleMapping = {};
                data.results.forEach(module => moduleMapping[module.ID] = module.Modulename);
                setModuleMap(moduleMapping);
            }
        } catch (err) {
            console.error('Failed to fetch modules:', err);
        }
    };

    /** ✅ Fetch Member Modules */
    const fetchMemberModules = async (token, page, outlet) => {
        setLoading(true);
        try {
            const bodyData = { page, page_size: 10 };
            if (outlet) bodyData.id_outlet = parseInt(outlet);

            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}membermodules`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(bodyData)
            });

            if (response.status === 401) {
                localStorage.removeItem('sessionToken');
                navigate('/loginadmin');
                return;
            }

            const data = await response.json();
            if (data.error === 0 && Array.isArray(data.data)) {
                const enrichedModules = data.data.map(module => ({
                    ...module,
                    outlet_name: outletMap[module.id_outlet] || `ID: ${module.id_outlet}`,
                    member_name: memberMap[module.id_member] || `ID: ${module.id_member}`,
                    modules_list: module.modules.map(moduleId => moduleMap[moduleId] || `ID: ${moduleId}`).join(', ') || "No Modules"
                }));

                setModules(enrichedModules);
                setFilteredModules(enrichedModules);
                setTotalRows(data.total_rows);
            } else {
                setModules([]);
                setFilteredModules([]);
                setTotalRows(0);
            }
        } catch (err) {
            console.error('Failed to fetch member modules:', err);
        } finally {
            setLoading(false);
        }
    };

    /** ✅ Handle Delete */
    const handleDelete = async (id) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "This action cannot be undone!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                const token = localStorage.getItem('sessionToken');
                try {
                    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}membermodule`, {
                        method: 'DELETE',
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                        body: JSON.stringify({ id })
                    });
                    const data = await response.json();
                    if (data.error === 0) {
                        Swal.fire('Deleted!', 'Member module has been deleted.', 'success');
                        fetchMemberModules(token, page, selectedOutlet);
                    } else {
                        Swal.fire('Error!', 'Failed to delete member module.', 'error');
                    }
                } catch (err) {
                    console.error('Failed to delete member module:', err);
                    Swal.fire('Error!', 'Something went wrong.', 'error');
                }
            }
        });
    };

    /** ✅ Table Columns */
    const columns = [
        { name: 'ID', selector: row => row.id, sortable: true, width: '80px' },
        { name: 'Outlet', selector: row => row.outlet_name, sortable: true },
        { name: 'Member', selector: row => row.member_name, sortable: true },
        { name: 'Modules', selector: row => row.modules_list, sortable: true },
        {
            name: 'Actions',
            cell: row => (
                <div className="d-flex gap-2">
                    <Button variant="outline-warning" size="sm" onClick={() => navigate(`/edit-member-module/${row.id}`)}>
                        <FaEdit /> Edit
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={() => handleDelete(row.id)}>
                        <FaTrash /> Delete
                    </Button>
                </div>
            ),
            width: '200px'
        }
    ];

    return (
        <div>
            <Navigation />
            <Container fluid className="admin-page-shell mt-4 px-4">
                <AdminPageHeader
                    icon={FaUserFriends}
                    eyebrow="Member access"
                    title="Member Module Assignment"
                    description="Kelola penetapan modul untuk member berdasarkan outlet agar layanan dan akses berjalan sesuai ketentuan."
                    breadcrumbs={[
                        { label: 'Dashboard', to: '/dashboardadmin' },
                        { label: 'Member Module Assignment' },
                    ]}
                    actions={(
                        <Button variant="primary" onClick={() => navigate('/add-member-module')}>
                            <FaPlus /> Assign Module
                        </Button>
                    )}
                    meta={(
                        <span className="soft-stat">
                            {filteredModules.length} assignments
                        </span>
                    )}
                />

                <div className="section-card table-card">
                  <div className="table-toolbar">
                    <span className="status-chip">{filteredModules.length} assignments</span>
                    <Row className="mb-3">
                        <Col md={4}>
                            <Form.Select value={selectedOutlet} onChange={(e) => setSelectedOutlet(e.target.value)}>
                                {Object.entries(outletMap).map(([id, name]) => (
                                    <option key={id} value={id}>{name}</option>
                                ))}
                            </Form.Select>
                        </Col>
                    </Row>
                  </div>
                  <div className="card-body pt-0">
                    <AdminDataTable
                      columns={columns}
                      data={filteredModules}
                      pagination
                      progressPending={loading}
                      emptyTitle="No member module assignments found"
                      emptyDescription="Tambahkan penetapan modul member untuk mengelola akses berdasarkan outlet."
                    />
                  </div>
                </div>
            </Container>
        </div>
    );
}

export default MemberModules;
